/**
 * IconMap — shared dynamic icon utilities
 *
 * Provides two regex-based lookup helpers:
 *  - `getSectionIcon(key)`  → icon for SectionCard titles  (w-3.5 h-3.5)
 *  - `getLabelIcon(key)`    → icon for StatCell labelIcons  (size 10, faint)
 *
 * Import from "@/components/atoms" or directly from "@/components/atoms/IconMap".
 */

import React from 'react';
import {
  FiFileText,
  FiClock,
  FiLink,
  FiHash,
  FiArrowRight,
  FiSend,
  FiInbox,
  FiAlertCircle,
  FiSettings,
  FiDatabase,
  FiUser,
  FiUsers,
  FiUserCheck,
  FiUserX,
  FiLogIn,
  FiLogOut,
  FiPackage,
  FiActivity,
  FiCode,
  FiServer,
  FiInfo,
  FiLayers,
  FiTag,
  FiToggleRight,
  FiToggleLeft,
  FiGlobe,
  FiKey,
  FiCpu,
  FiMapPin,
  FiCheckCircle,
  FiList,
  FiBox,
  FiPhone,
  FiMail,
  FiPercent,
  FiDollarSign,
  FiSmartphone,
  FiFile,
  FiShield,
  FiFlag,
  FiRepeat,
  FiWifi,
  FiTrendingUp,
  FiAnchor,
  FiTablet,
  FiCalendar,
  FiRefreshCw,
  FiUpload,
  FiDownload,
  FiPaperclip,
  FiImage,
  FiCamera,
  FiVideo,
  FiMusic,
  FiMic,
  FiVoicemail,
  FiPrinter,
  FiMonitor,
  FiHardDrive,
  FiTerminal,
  FiTruck,
  FiNavigation,
  FiNavigation2,
  FiCompass,
  FiShoppingCart,
  FiBriefcase,
  FiCreditCard,
  FiGift,
  FiHome,
  FiStar,
  FiHeart,
  FiThumbsUp,
  FiThumbsDown,
  FiMessageSquare,
  FiMessageCircle,
  FiBell,
  FiBellOff,
  FiSearch,
  FiFilter,
  FiZap,
  FiLock,
  FiUnlock,
  FiEye,
  FiEyeOff,
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiMinus,
  FiX,
  FiCheck,
  FiMaximize,
  FiMinimize,
  FiExternalLink,
  FiCopy,
  FiScissors,
  FiClipboard,
  FiGrid,
  FiColumns,
  FiSidebar,
  FiBarChart,
  FiBarChart2,
  FiPieChart,
  FiMap,
  FiTool,
  FiSliders,
  FiAlertTriangle,
  FiXCircle,
  FiHelpCircle,
  FiLoader,
  FiPower,
  FiPause,
  FiPlay,
  FiSkipForward,
  FiBookmark,
  FiBookOpen,
} from 'react-icons/fi';

// ─── Section icons (used on SectionCard title) ────────────────────────────────
const SECTION_ICON_MAP: [RegExp, React.ReactElement][] = [
  [/request/i, <FiSend className="w-3.5 h-3.5" />],
  [/response/i, <FiInbox className="w-3.5 h-3.5" />],
  [/message|chat|notification/i, <FiMessageSquare className="w-3.5 h-3.5" />],
  [/error|exception|fail|fault/i, <FiAlertCircle className="w-3.5 h-3.5" />],
  [/warning|warn/i, <FiAlertTriangle className="w-3.5 h-3.5" />],
  [/config|setting|option|param/i, <FiSettings className="w-3.5 h-3.5" />],
  [/filter|criteria/i, <FiFilter className="w-3.5 h-3.5" />],
  [/data|record|result/i, <FiDatabase className="w-3.5 h-3.5" />],
  [/report|analytics|stat|metric/i, <FiBarChart2 className="w-3.5 h-3.5" />],
  [/user|account|profile|member/i, <FiUser className="w-3.5 h-3.5" />],
  [/team|group|org|staff/i, <FiUsers className="w-3.5 h-3.5" />],
  [/payload|body|content|raw/i, <FiCode className="w-3.5 h-3.5" />],
  [/package|product|item|inventory/i, <FiPackage className="w-3.5 h-3.5" />],
  [/cart|order|purchase/i, <FiShoppingCart className="w-3.5 h-3.5" />],
  [/event|action|activity|audit|log/i, <FiActivity className="w-3.5 h-3.5" />],
  [/server|host|service|cluster/i, <FiServer className="w-3.5 h-3.5" />],
  [/network|wifi|connection|socket/i, <FiWifi className="w-3.5 h-3.5" />],
  [/time|date|schedule|calendar/i, <FiClock className="w-3.5 h-3.5" />],
  [/timeline|history|journal/i, <FiBookOpen className="w-3.5 h-3.5" />],
  [/link|url|endpoint|route/i, <FiLink className="w-3.5 h-3.5" />],
  [/navigation|location|map|geo/i, <FiNavigation className="w-3.5 h-3.5" />],
  [/info|detail|summary|meta/i, <FiInfo className="w-3.5 h-3.5" />],
  [
    /provider|vendor|partner|tenant|merchant/i,
    <FiAnchor className="w-3.5 h-3.5" />,
  ],
  [/device|hardware|cooler|machine/i, <FiSmartphone className="w-3.5 h-3.5" />],
  [/system|platform|env|os/i, <FiCpu className="w-3.5 h-3.5" />],
  [/identity|auth|credential/i, <FiLock className="w-3.5 h-3.5" />],
  [/role|permission|security/i, <FiShield className="w-3.5 h-3.5" />],
  [
    /location|address|region|market|zone/i,
    <FiMapPin className="w-3.5 h-3.5" />,
  ],
  [
    /payment|transaction|billing|finance/i,
    <FiCreditCard className="w-3.5 h-3.5" />,
  ],
  [
    /price|cost|amount|balance|revenue/i,
    <FiDollarSign className="w-3.5 h-3.5" />,
  ],
  [/file|document|report|attachment/i, <FiFileText className="w-3.5 h-3.5" />],
  [/image|photo|picture/i, <FiImage className="w-3.5 h-3.5" />],
  [/video|media/i, <FiVideo className="w-3.5 h-3.5" />],
  [/upload|import/i, <FiUpload className="w-3.5 h-3.5" />],
  [/download|export/i, <FiDownload className="w-3.5 h-3.5" />],
  [/diagnostic|health|monitor|ping/i, <FiActivity className="w-3.5 h-3.5" />],
  [/ship|deliver|logistics|dispatch/i, <FiTruck className="w-3.5 h-3.5" />],
  [/notif(ication)?|alert/i, <FiBell className="w-3.5 h-3.5" />],
  [/search|discovery/i, <FiSearch className="w-3.5 h-3.5" />],
];

export const getSectionIcon = (key: string): React.ReactElement => {
  for (const [re, icon] of SECTION_ICON_MAP) {
    if (re.test(key)) return icon;
  }
  return <FiFileText className="w-3.5 h-3.5" />;
};

// ─── Label icons (used on StatCell labelIcon) ─────────────────────────────────
const S = 10;
const CX = 'shrink-0 opacity-60';

const LABEL_ICON_MAP: [RegExp, React.ReactElement][] = [
  // ── Identifiers ────────────────────────────────────────────────────────
  [/^id$|_id$|uuid|uid/i, <FiHash size={S} className={CX} />],
  [/ id$|id$/i, <FiHash size={S} className={CX} />],
  [/key$|token|secret|apikey|api_key/i, <FiKey size={S} className={CX} />],
  [/name$|label|title|alias|slug/i, <FiTag size={S} className={CX} />],
  [/code$|number$|no$|num$/i, <FiHash size={S} className={CX} />],
  [/ref$|reference|correlat/i, <FiArrowRight size={S} className={CX} />],

  // ── Network / URL ──────────────────────────────────────────────────────
  [/url|link|endpoint|path|route|uri/i, <FiLink size={S} className={CX} />],
  [/ip|host|domain|port|socket/i, <FiGlobe size={S} className={CX} />],
  [/server|service/i, <FiServer size={S} className={CX} />],
  [
    /protocol|scheme|transport|http|https|grpc|mqtt|amqp/i,
    <FiGlobe size={S} className={CX} />,
  ],
  [
    /refer|referral|referer|referrer/i,
    <FiArrowRight size={S} className={CX} />,
  ],
  [/network|wifi|signal|ssid|bandwidth/i, <FiWifi size={S} className={CX} />],
  [/proxy|gateway|relay|tunnel/i, <FiNavigation2 size={S} className={CX} />],
  [/dns|resolver|nameserver/i, <FiGlobe size={S} className={CX} />],

  // ── Location / Geography ───────────────────────────────────────────────
  [
    /location|address|region|zone|market|city|country|state|postal|zip/i,
    <FiMapPin size={S} className={CX} />,
  ],
  [
    /latitude|longitude|lat$|lon$|lng$|coord/i,
    <FiCompass size={S} className={CX} />,
  ],
  [/timezone|tz$/i, <FiGlobe size={S} className={CX} />],
  [/distance|radius/i, <FiNavigation size={S} className={CX} />],

  // ── Time / Dates ───────────────────────────────────────────────────────
  [
    /timestamp|datetime|createdat|updatedat|deletedat|dtm|_at$|_on$/i,
    <FiClock size={S} className={CX} />,
  ],
  [/time$/i, <FiClock size={S} className={CX} />],
  [/date$/i, <FiCalendar size={S} className={CX} />],
  [
    /duration|interval|timeout|delay|ttl$/i,
    <FiClock size={S} className={CX} />,
  ],
  [/start|begin|open(ed)?$/i, <FiCalendar size={S} className={CX} />],
  [/end$|finish|close(d)?$|expir/i, <FiCalendar size={S} className={CX} />],
  [/range|period|span|window/i, <FiArrowRight size={S} className={CX} />],
  [/schedule|cron|recur/i, <FiRepeat size={S} className={CX} />],

  // ── People / Auth ──────────────────────────────────────────────────────
  [
    /user(name)?|account|profile|operator|owner|assign/i,
    <FiUser size={S} className={CX} />,
  ],
  [/team|group|member|staff|employee/i, <FiUsers size={S} className={CX} />],
  [
    /created_?by|updated_?by|modified_?by|author/i,
    <FiUserCheck size={S} className={CX} />,
  ],
  [
    /deleted_?by|removed_?by|deactivated_?by/i,
    <FiUserX size={S} className={CX} />,
  ],
  [/login|signin|logged/i, <FiLogIn size={S} className={CX} />],
  [/logout|signout/i, <FiLogOut size={S} className={CX} />],
  [/session|cookie|jwt/i, <FiKey size={S} className={CX} />],

  // ── Provider / Vendor ──────────────────────────────────────────────────
  [
    /provider|vendor|partner|tenant|merchant|reseller/i,
    <FiAnchor size={S} className={CX} />,
  ],

  // ── Status / State / Flags ────────────────────────────────────────────
  [/status|state|phase|stage$/i, <FiActivity size={S} className={CX} />],
  [
    /enabled|active|online|live|running/i,
    <FiToggleRight size={S} className={CX} />,
  ],
  [
    /disabled|inactive|offline|stopped/i,
    <FiToggleLeft size={S} className={CX} />,
  ],
  [
    /success|valid|confirmed|approved|passed/i,
    <FiCheckCircle size={S} className={CX} />,
  ],
  [/failed?|error|exception|fault/i, <FiXCircle size={S} className={CX} />],
  [/warning|warn/i, <FiAlertTriangle size={S} className={CX} />],
  [/pending|waiting|queue|in_?progress/i, <FiLoader size={S} className={CX} />],
  [/cancelled?|aborted?|rejected/i, <FiX size={S} className={CX} />],
  [/priority|severity|level|rank|urgency/i, <FiFlag size={S} className={CX} />],
  [/lock(ed)?|frozen/i, <FiLock size={S} className={CX} />],
  [/unlock(ed)?/i, <FiUnlock size={S} className={CX} />],
  [/publish(ed)?|public/i, <FiCheckCircle size={S} className={CX} />],
  [/draft|unpublish|private/i, <FiEyeOff size={S} className={CX} />],
  [/visible|show|hidden|flag/i, <FiEye size={S} className={CX} />],

  // ── Config / Type / Version ───────────────────────────────────────────
  [
    /config|setting|option|param|preference/i,
    <FiSettings size={S} className={CX} />,
  ],
  [
    /type|kind|category|class|mode|variant|format/i,
    <FiLayers size={S} className={CX} />,
  ],
  [/version|revision|build|release|tag$/i, <FiCode size={S} className={CX} />],
  [
    /method|action|operation|command|verb/i,
    <FiActivity size={S} className={CX} />,
  ],
  [/env|environment|deployment|cluster/i, <FiCpu size={S} className={CX} />],
  [/feature|toggle|experiment/i, <FiToggleRight size={S} className={CX} />],
  [/template|schema|model$/i, <FiGrid size={S} className={CX} />],
  [/rule|policy|condition|criteria/i, <FiSliders size={S} className={CX} />],

  // ── Data / Content ────────────────────────────────────────────────────
  [
    /message|description|note|text|body|comment|summary|details/i,
    <FiInfo size={S} className={CX} />,
  ],
  [
    /price$|amount$|gross|net total|value$|total price|product price/i,
    <FiDollarSign size={S} className={CX} />,
  ],
  [/total$|subtotal$/i, <FiDollarSign size={S} className={CX} />],
  [/count$|qty$|quantity$/i, <FiList size={S} className={CX} />],
  [
    /amount|price|total|qty|quantity/i,
    <FiDollarSign size={S} className={CX} />,
  ],
  [/count|size|weight|volume/i, <FiList size={S} className={CX} />],
  [/list|items|array|values|entries|rows/i, <FiList size={S} className={CX} />],
  [/package|product|item|sku|catalog/i, <FiPackage size={S} className={CX} />],
  [/payload|content|data|record|object/i, <FiBox size={S} className={CX} />],
  [/meta(data)?$/i, <FiFileText size={S} className={CX} />],
  [/tag$|tags$|labels$/i, <FiTag size={S} className={CX} />],
  [/attribute|property|field|column/i, <FiColumns size={S} className={CX} />],

  // ── Direction / Flow ──────────────────────────────────────────────────
  [/send|outbound|push/i, <FiSend size={S} className={CX} />],
  [/receive|inbound|pull|fetch/i, <FiInbox size={S} className={CX} />],
  [/source|origin|from$/i, <FiSend size={S} className={CX} />],
  [/target|dest(ination)?|to$/i, <FiInbox size={S} className={CX} />],
  [/upload|import/i, <FiUpload size={S} className={CX} />],
  [/download|export/i, <FiDownload size={S} className={CX} />],
  [/request/i, <FiSend size={S} className={CX} />],
  [/response|reply/i, <FiInbox size={S} className={CX} />],
  [/forward|redirect/i, <FiArrowRight size={S} className={CX} />],

  // ── Contact ───────────────────────────────────────────────────────────
  [/phone|mobile|tel|cell/i, <FiPhone size={S} className={CX} />],
  [/email|mail/i, <FiMail size={S} className={CX} />],
  [/fax/i, <FiPrinter size={S} className={CX} />],
  [/voicemail|voice/i, <FiVoicemail size={S} className={CX} />],
  [/chat|message$/i, <FiMessageCircle size={S} className={CX} />],

  // ── Financial ─────────────────────────────────────────────────────────
  [/percent|rate|ratio|pct|tax/i, <FiPercent size={S} className={CX} />],
  [
    /currency|dollar|usd|eur|gbp|fee|cost|charge/i,
    <FiDollarSign size={S} className={CX} />,
  ],
  [
    /balance|revenue|earning|profit|income|sales/i,
    <FiTrendingUp size={S} className={CX} />,
  ],
  [
    /payment|transaction|billing|invoice|receipt/i,
    <FiCreditCard size={S} className={CX} />,
  ],
  [/discount|coupon|promo|offer/i, <FiGift size={S} className={CX} />],
  [/cart|order|basket|purchase/i, <FiShoppingCart size={S} className={CX} />],
  [/refund|chargeback|reversal/i, <FiRefreshCw size={S} className={CX} />],
  [/wallet/i, <FiDollarSign size={S} className={CX} />],

  // ── Device / Hardware ─────────────────────────────────────────────────
  [
    /device(id)?|serial|sn$|hardware|cooler/i,
    <FiSmartphone size={S} className={CX} />,
  ],
  [/reader|scanner|terminal|pos/i, <FiMonitor size={S} className={CX} />],
  [
    /free[\s_]?of|free(memory|heap|ram|disk|space)?$/i,
    <FiHardDrive size={S} className={CX} />,
  ],
  [
    /used(memory|heap|ram|disk|space)?$|usedof/i,
    <FiHardDrive size={S} className={CX} />,
  ],
  [
    /total(memory|heap|ram|disk|space)?$|totalof/i,
    <FiHardDrive size={S} className={CX} />,
  ],
  [
    /available(memory|heap|ram|disk|space)?$|avail/i,
    <FiHardDrive size={S} className={CX} />,
  ],
  [
    /heap(used|total|size)?|rss$|arraybuffers/i,
    <FiHardDrive size={S} className={CX} />,
  ],
  [
    /storage\s*used|storage\s*free|disk\s*used/i,
    <FiHardDrive size={S} className={CX} />,
  ],
  [
    /storage|disk|drive|hdd|ssd|memory|ram|mb$|gb$|kb$/i,
    <FiHardDrive size={S} className={CX} />,
  ],
  [/cpu|processor|chip|core/i, <FiCpu size={S} className={CX} />],
  [/battery|power|charge/i, <FiZap size={S} className={CX} />],
  [/camera|lens/i, <FiCamera size={S} className={CX} />],
  [/mic|microphone|audio/i, <FiMic size={S} className={CX} />],
  [/monitor|display|screen/i, <FiMonitor size={S} className={CX} />],
  [/printer|print/i, <FiPrinter size={S} className={CX} />],

  // ── Platform / SDK / App ──────────────────────────────────────────────
  [/android|sdk|apk|gradle/i, <FiTablet size={S} className={CX} />],
  [/ios|iphone|ipad|swift/i, <FiSmartphone size={S} className={CX} />],
  [/platform|os|firmware|runtime|kernel/i, <FiCpu size={S} className={CX} />],
  [/app(lication)?|bundle/i, <FiSmartphone size={S} className={CX} />],

  // ── Files / Media ─────────────────────────────────────────────────────
  [
    /file|filename|format|mime|ext|attachment/i,
    <FiFile size={S} className={CX} />,
  ],
  [
    /image|photo|picture|thumbnail|avatar/i,
    <FiImage size={S} className={CX} />,
  ],
  [/video|clip|recording/i, <FiVideo size={S} className={CX} />],
  [/audio|sound|music|track/i, <FiMusic size={S} className={CX} />],
  [/document|pdf|doc|report/i, <FiFileText size={S} className={CX} />],
  [/clipboard|paste/i, <FiClipboard size={S} className={CX} />],
  [/copy|duplicate/i, <FiCopy size={S} className={CX} />],

  // ── Security / Auth ───────────────────────────────────────────────────
  [
    /role|permission|scope|access|privilege/i,
    <FiShield size={S} className={CX} />,
  ],
  [/password|passphrase|pin$/i, <FiLock size={S} className={CX} />],
  [
    /checksum|hash|digest|md5|sha|crc|signature/i,
    <FiShield size={S} className={CX} />,
  ],
  [/certificate|cert|ssl|tls|x509/i, <FiShield size={S} className={CX} />],
  [/otp|2fa|mfa|verify/i, <FiKey size={S} className={CX} />],

  // ── Operational ───────────────────────────────────────────────────────
  [/retry|attempt|iteration|repeat/i, <FiRepeat size={S} className={CX} />],
  [/index|offset|page|limit|skip|cursor/i, <FiList size={S} className={CX} />],
  [/channel|queue|topic|stream|pipe/i, <FiActivity size={S} className={CX} />],
  [/job|task|worker|thread|process/i, <FiLoader size={S} className={CX} />],
  [/trigger|hook|webhook|event$/i, <FiZap size={S} className={CX} />],
  [/log|trace|debug|audit/i, <FiTerminal size={S} className={CX} />],
  [/health|ping|heartbeat|uptime/i, <FiActivity size={S} className={CX} />],
  [/metric|perf|benchmark|latency/i, <FiBarChart2 size={S} className={CX} />],
  [/sync|replicate|mirror/i, <FiRefreshCw size={S} className={CX} />],
  [/backup|snapshot/i, <FiHardDrive size={S} className={CX} />],
  [/restart|reboot|reset/i, <FiRefreshCw size={S} className={CX} />],
  [/power|on_?off/i, <FiPower size={S} className={CX} />],

  // ── Brand / Manufacturer ──────────────────────────────────────────────
  [/brand|make|manufacturer|oem|model/i, <FiBox size={S} className={CX} />],

  // ── Shipping / Logistics ──────────────────────────────────────────────
  [
    /ship|deliver|dispatch|carrier|courier|freight/i,
    <FiTruck size={S} className={CX} />,
  ],
  [/track(ing)?$/i, <FiNavigation size={S} className={CX} />],
  [/warehouse|inventory|stock/i, <FiPackage size={S} className={CX} />],

  // ── Notifications / Alerts ────────────────────────────────────────────
  [/notif(ication)?|alert$/i, <FiBell size={S} className={CX} />],
  [/mute|silent|no_?notif/i, <FiBellOff size={S} className={CX} />],

  // ── Ratings / Feedback ────────────────────────────────────────────────
  [/rating|score|star|review/i, <FiStar size={S} className={CX} />],
  [/like|love|heart|favourite/i, <FiHeart size={S} className={CX} />],
  [/upvote|thumbs_?up/i, <FiThumbsUp size={S} className={CX} />],
  [/downvote|thumbs_?down/i, <FiThumbsDown size={S} className={CX} />],

  // ── Search / Discovery ────────────────────────────────────────────────
  [/search|query|find|lookup/i, <FiSearch size={S} className={CX} />],
  [/filter|sort|order_?by/i, <FiFilter size={S} className={CX} />],

  // ── Edit / CRUD ───────────────────────────────────────────────────────
  [/edit|update|modify|change|patch/i, <FiEdit2 size={S} className={CX} />],
  [/create|add|insert|new$/i, <FiPlus size={S} className={CX} />],
  [/delete|remove|destroy|purge/i, <FiTrash2 size={S} className={CX} />],
  [/read|view|get|fetch|load/i, <FiEye size={S} className={CX} />],

  // ── Misc ──────────────────────────────────────────────────────────────
  [/home|dashboard|main/i, <FiHome size={S} className={CX} />],
  [/office|work|business|company/i, <FiBriefcase size={S} className={CX} />],
  [/help|support|faq|unknown/i, <FiHelpCircle size={S} className={CX} />],
  [/bookmark|saved|pinned/i, <FiBookmark size={S} className={CX} />],
  [/tool|utility|helper/i, <FiTool size={S} className={CX} />],
  [/external|outlink/i, <FiExternalLink size={S} className={CX} />],
  [/expand|maximize|fullscreen/i, <FiMaximize size={S} className={CX} />],
  [/collapse|minimize/i, <FiMinimize size={S} className={CX} />],
  [/cut|trim|clip/i, <FiScissors size={S} className={CX} />],
  [/skip|next|advance/i, <FiSkipForward size={S} className={CX} />],
];

export const getLabelIcon = (key: string): React.ReactElement | undefined => {
  for (const [re, icon] of LABEL_ICON_MAP) {
    if (re.test(key)) return icon;
  }
  return undefined;
};
